import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  computed,
  makeEnvironmentProviders,
  type EnvironmentProviders,
} from '@angular/core';
import {
  RouterCancel,
  RouterDataResolved,
  RouterError,
  RouterNavigated,
  RouterNavigation,
  RouterRequest,
  withNgxsRouterPlugin,
  type NavigationActionTiming,
} from '@ngxs/router-plugin';
import {
  LOCAL_STORAGE_ENGINE,
  SESSION_STORAGE_ENGINE,
  withNgxsStoragePlugin,
} from '@ngxs/storage-plugin';
import {
  provideStore,
  select,
  withNgxsDevelopmentOptions,
  withNgxsPlugin,
} from '@ngxs/store';

import { AsiAuthService } from '@asi/auth/data-access-auth';
import { provideCosFormField } from '@cosmos/components/form-field/config';
import { provideAppSettingsDataAccess } from '@cosmos/data-access-app-settings';
import { provideMeta } from '@cosmos/meta';
import { provideNgEventPlugins } from '@cosmos/ng-event-plugins';
import { NgxsWithLazyStatePlugin } from '@cosmos/state/lazy-registration';
import { provideToast } from '@cosmos/ui-notification-toast';
import { isEqual } from '@cosmos/util-common/is-equal';
import { COS_DATE_LOCALE } from '@cosmos/util-datetime-config';
import { provideWebDownload } from '@cosmos/util-download/web';
import { provideAsyncTippyLoader } from '@cosmos/util-tippy-config';
import {
  CUSTOMER_PORTAL_AUTH_STATE_TOKEN,
  provideCustomerPortalAuthDataAccess,
} from '@customer-portal/auth/data-access-auth';
import {
  CUSTOMER_PORTAL_COLLECTION_AUTH_STATE_TOKEN,
  provideCustomerPortalCollectionAuthDataAccess,
} from '@customer-portal/collections/data-access-auth';
import {
  CUSTOMER_PORTAL_PRESENTATION_PRODUCT_FEEDBACK_STATE_TOKEN,
  provideDataAccessPresentationProductFeedback,
} from '@customer-portal/presentations/data-access-presentation-product-feedback';
import {
  CustomerPortalPresentationQueries,
  CustomerPortalProductRequestApiService,
  provideCustomerPortalPresentationsDataAccess,
} from '@customer-portal/presentations/data-access-presentations';
import { provideCustomerPortalProjectDataAccess } from '@customer-portal/projects/data-access/store';
import { provideEspLookupTypesDataAccess } from '@esp/lookup/data-access-lookup';
import { provideCustomRouterStateSerializer } from '@esp/router';
import {
  CONSUMER_CONTACT_INFO_STATE_TOKEN,
  provideDataAccessConsumerContactInfo,
} from '@website-modules/data-access-consumer-contact-info';
import { provideProductRequest } from '@website-modules/product-requests/feature-product-requests-panel';

import { environment } from '../../environments/environment';

import { AuthInterceptor } from './interceptors';
import { provideImageLoader } from './providers/image-loader.provider';
import { CustomerPortalAuthService } from './services';

const NavigationActionTiming__PostActivation: NavigationActionTiming.PostActivation = 2;

const ngxsFeatures = [
  withNgxsRouterPlugin({
    navigationActionTiming: NavigationActionTiming__PostActivation,
  }),
  withNgxsStoragePlugin({
    namespace: 'portal',
    keys: [
      {
        key: CUSTOMER_PORTAL_AUTH_STATE_TOKEN,
        engine: SESSION_STORAGE_ENGINE,
      },
      {
        key: CUSTOMER_PORTAL_COLLECTION_AUTH_STATE_TOKEN,
        engine: SESSION_STORAGE_ENGINE,
      },
      {
        key: 'appSettings',
        engine: LOCAL_STORAGE_ENGINE,
      },
      {
        key: CUSTOMER_PORTAL_PRESENTATION_PRODUCT_FEEDBACK_STATE_TOKEN,
        engine: LOCAL_STORAGE_ENGINE,
      },
      {
        key: CONSUMER_CONTACT_INFO_STATE_TOKEN,
        engine: LOCAL_STORAGE_ENGINE,
      },
    ],
  }),
  withNgxsPlugin(NgxsWithLazyStatePlugin),
];

if (ngDevMode) {
  ngxsFeatures.push(
    withNgxsDevelopmentOptions({
      warnOnDuplicateActionTypes: true,
      warnOnNewReferenceWithIdenticalValue: { isEqual },
      warnOnUnhandledActions: {
        // These actions are dispatched by the router plugin, but we don't
        // handle them explicitly in any state. We're safe to ignore them
        // from being warned.
        ignore: [
          RouterRequest,
          RouterNavigation,
          RouterCancel,
          RouterError,
          RouterDataResolved,
          RouterNavigated,
        ],
      },
    })
  );
}

export function makeCoreEnvironmentProviders(): EnvironmentProviders[] {
  const providers = [
    provideStore(
      [],
      {
        developmentMode: !environment.production,
        selectorOptions: {
          suppressErrors: environment.production,
        },
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
      ...ngxsFeatures
    ),

    provideAppSettingsDataAccess(),
    provideCosFormField(),
    provideEspLookupTypesDataAccess(),
    provideCustomerPortalAuthDataAccess(),
    provideCustomerPortalCollectionAuthDataAccess(),
    provideCustomerPortalPresentationsDataAccess(),
    provideDataAccessConsumerContactInfo(() => {
      const presentationId = select(
        CustomerPortalPresentationQueries.getPresentationId
      );
      return computed(() => presentationId()?.toString() ?? null);
    }),
    provideDataAccessPresentationProductFeedback(),
    provideProductRequest({
      productRequestApiService: CustomerPortalProductRequestApiService,
      productRequestPanelConfig: {
        rememberConsumerContactInfo: true,
        requirePhone: true,
        loadProductCardComponent: () =>
          import(
            '@customer-portal/presentations/feature-product-requests-panel'
          ).then(
            ({ CustomerPortalProductRequestProductCardComponent }) =>
              CustomerPortalProductRequestProductCardComponent
          ),
      },
    }),
    provideCustomerPortalProjectDataAccess(),

    provideAsyncTippyLoader(),
    {
      provide: AsiAuthService,
      useExisting: CustomerPortalAuthService,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideCustomRouterStateSerializer(),
    { provide: COS_DATE_LOCALE, useValue: 'en-US' },
    provideWebDownload(),
    ...(environment.providers || []),
    provideNgEventPlugins(),
    provideMeta({
      applicationName: 'Portal',
      pageTitleSeparator: ' - ',
      defaults: {
        title: 'Portal',
      },
    }),
    provideToast(),
    provideImageLoader(),
  ];

  return [makeEnvironmentProviders(providers)];
}
